import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

const RTOHistory = () => {
    const [historyData, setHistoryData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const fetchHistoryData = async () => {
            try {
                setLoading(true);
                setError('');
                const response = await fetch('https://api.dremerz.net/api/DZ_8923_RTOProject_RTOTable_RTOSoldVehicleNumbers');
                
                if (!response.ok) {
                    throw new Error('Failed to fetch history data');
                }
                
                const data = await response.json();
                // Reverse the array to show most recent entries first
                setHistoryData(data.reverse());
            } catch (err) {
                console.error('Error fetching history data:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchHistoryData();
    }, []);

    const handleDelete = async (id) => {
        try {
            setLoading(true);
            setError('');
            
            const response = await fetch(`https://api.dremerz.net/api/DZ_8923_RTOProject_RTOTable_RTOSoldVehicleNumbers/${id}`, {
                method: 'DELETE'
            });
            
            if (!response.ok) {
                throw new Error('Failed to delete record');
            }
            
            // Update the local state to remove the deleted record
            setHistoryData(prevData => prevData.filter(record => record.id !== id));
            setSuccessMessage('Record deleted successfully!');
            
            setTimeout(() => {
                setSuccessMessage('');
            }, 3000);
        } catch (err) {
            console.error('Error deleting record:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger m-3">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                {error}
            </div>
        );
    }

    return (
        <div className="container-fluid px-4 mt-4">
            <div className="card shadow">
                <div className="card-header text-white" style={{        background: 'linear-gradient(145deg, #1a2980, #26d0ce)',
}}>
                    <h2 className="mb-0">
                        <i className="bi bi-clock-history me-2"></i>
                        Complete Vehicle Number History
                    </h2>
                    <button 
                        className="btn btn-light btn-sm mt-2"
                        onClick={() => navigate('/')}
                    >
                        <i className="bi bi-arrow-left me-1"></i>
                        Back to Home
                    </button>
                </div>
                
                <div className="card-body">
                    {successMessage && (
                        <div className="alert alert-success">
                            <i className="bi bi-check-circle-fill me-2"></i>
                            {successMessage}
                        </div>
                    )}
                    
                    {historyData.length === 0 ? (
                        <div className="alert alert-info">
                            <i className="bi bi-info-circle-fill me-2"></i>
                            No history records found.
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-striped table-hover" style={{ width: '100%' }}>
                                <thead className="table-dark">
                                    <tr>
                                        <th style={{ width: '5%' }}>ID</th>
                                        <th style={{ width: '15%' }}>RTO Number</th>
                                        <th style={{ width: '50%' }}>Sold Vehicle Numbers</th>
                                        <th style={{ width: '10%' }}>Count</th>
                                        <th style={{ width: '20%' }}>Created Date</th>
                                        <th style={{ width: '20%' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {historyData.map((record) => {
                                        const numbers = record.SoldVehicleNum.split(/\s+/).filter(num => num.trim() !== '');
                                        return (
                                            <tr key={record.id}>
                                                <td>{record.id}</td>
                                                <td>
                                                    <span 
                                                        className="text-primary" 
                                                        style={{cursor: 'pointer'}}
                                                        onClick={() => navigate(`/rto/${record.RTONum}`)}
                                                    >
                                                        {record.RTONum}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div style={{ 
                                                        maxHeight: '100px', 
                                                        overflowY: 'auto',
                                                        padding: '5px',
                                                        backgroundColor: '#f8f9fa',
                                                        borderRadius: '4px',
                                                        width: '100%',
                                                        maxWidth: '500px'
                                                    }}>
                                                        {numbers.join(', ')}
                                                    </div>
                                                </td>
                                                <td>{numbers.length}</td>
                                                <td>{formatDate(record.createdAt)}</td>
                                                <td>
                                                    <button 
                                                        className="btn btn-danger btn-sm"
                                                        onClick={() => handleDelete(record.id)}
                                                        disabled={loading}
                                                    >
                                                        {loading ? (
                                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                        ) : (
                                                            <>
                                                                <i className="bi bi-trash-fill me-1"></i>
                                                                Delete
                                                            </>
                                                        )}
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
            
            <style jsx>{`
                .table-responsive {
                    overflow-x: auto;
                }
                th {
                    position: sticky;
                    top: 0;
                    background-color: #343a40;
                    z-index: 10;
                }
                .table {
                    margin-bottom: 0;
                }
            `}</style>
        </div>
    );
};

export default RTOHistory;